/**
 * Folder List View - Table-based list display mode
 *
 * Selection is managed centrally via useSelectionStore.
 * This component only renders items and passes selection state down.
 *
 * @module features/workspace/components/FolderView/FolderListView
 */

import { useRef, useEffect, useMemo } from 'react';
import type { WorkspaceItemType } from '@loader/shared-types';

import type { WorkspaceItem, SortBy, SortDir } from './types';
import { ColumnHeader } from './ListTableComponents';
import { ListTableRow } from './ListTableRow';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useSelectionPreviewSync } from '../../hooks/useSelectionPreviewSync';

interface FolderListViewProps {
  items: WorkspaceItem[];
  sortBy: SortBy;
  sortDir: SortDir;
  isTrash?: boolean;
  /** Whether user can edit (editor/owner) - viewers cannot modify items */
  canEdit?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onSortChange: (sortBy: SortBy) => void;
  // Note: onRename is handled internally by ListTableRow via useRenameLogic hook
  onDuplicate?: (id: string) => void;
  onToggleFavorite?: (id: string, type: WorkspaceItemType) => void;
  onMove?: (id: string, type: WorkspaceItemType) => void;
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
  onRestore?: (id: string, type: WorkspaceItemType) => void;
  onDeletePermanently?: (id: string, type: WorkspaceItemType) => void;
  onPreview?: (id: string, type: WorkspaceItemType) => void;
  onResumeDraft?: (id: string) => void;
  /** Drag and drop handlers */
  onDragStart?: (e: React.DragEvent, id: string, type: WorkspaceItemType) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  isDraggingItem?: (id: string) => boolean;
  isDraggingOverFolder?: (id: string) => boolean;
}

/**
 * OS Explorer-style list table
 */
export function FolderListView({
  items,
  sortBy,
  sortDir,
  isTrash,
  canEdit = true,
  onKeyDown,
  onSortChange,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
  onPreview,
  onResumeDraft,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDraggingItem,
  isDraggingOverFolder,
}: FolderListViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Selection store - single source of truth
  const { isSelected, lastSelectedId } = useSelectionStore();

  // Re-open preview when going from multi-select back to single selection
  useSelectionPreviewSync({ items, onPreview });

  // Compute all item IDs for range selection
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);

  // Scroll selected item into view when selection changes via keyboard
  useEffect(() => {
    if (lastSelectedId) {
      const element = itemRefs.current.get(lastSelectedId);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [lastSelectedId]);

  return (
    <div
      ref={containerRef}
      className="min-h-0 flex-1 overflow-auto rounded-t-lg border-x border-t"
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <table className="w-full min-w-max">
        <thead className="bg-muted sticky top-0 z-10 border-b">
          <tr>
            <ColumnHeader
              label="Name"
              sortKey="name"
              currentSortBy={sortBy}
              currentSortDir={sortDir}
              onSort={onSortChange}
              resizable={false}
            />
            <th className="w-20 px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
              Status
            </th>
            <ColumnHeader
              label="Kind"
              sortKey="type"
              currentSortBy={sortBy}
              currentSortDir={sortDir}
              onSort={onSortChange}
              resizable={false}
              className="w-28"
            />
            <th className="w-24 px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
              Size
            </th>
            <ColumnHeader
              label="Date Created"
              sortKey="created_at"
              currentSortBy={sortBy}
              currentSortDir={sortDir}
              onSort={onSortChange}
              resizable={false}
              className="w-40 hidden lg:table-cell"
            />
            <ColumnHeader
              label="Date Modified"
              sortKey="updated_at"
              currentSortBy={sortBy}
              currentSortDir={sortDir}
              onSort={onSortChange}
              resizable={false}
              className="w-40"
            />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ListTableRow
              key={`${item.type}-${item.id}`}
              ref={(el) => {
                if (el) {
                  itemRefs.current.set(item.id, el);
                } else {
                  itemRefs.current.delete(item.id);
                }
              }}
              item={item}
              allItemIds={allItemIds}
              isTrash={isTrash}
              canEdit={canEdit}
              isSelected={isSelected(item.id)}
              onDuplicate={onDuplicate}
              onToggleFavorite={onToggleFavorite}
              onMove={onMove}
              onDelete={onDelete}
              onRestore={onRestore}
              onDeletePermanently={onDeletePermanently}
              onPreview={onPreview}
              onResumeDraft={onResumeDraft}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              isDragging={isDraggingItem?.(item.id)}
              isDropTarget={item.type === 'folder' && isDraggingOverFolder?.(item.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
